import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Avatar,
  Checkbox,
  OutlinedInput
} from "@mui/material";
import {
  Search as SearchIcon,
  Security as SecurityIcon,
  MenuBook as MenuIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  AdminPanelSettings as RoleIcon
} from "@mui/icons-material";
import {
  getAllUsers,
  getAllRoles,
  getUserRoles,
  assignRoleToUser,
  removeRoleFromUser,
  getMenusByRole,
  assignMenuToRole,
  removeMenuFromRole,
  fetchAllMenus,
} from "../services/api";
import { useTranslation } from "react-i18next";

export default function RoleMenuManagement() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleMenus, setRoleMenus] = useState([]);
  const [allMenus, setAllMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedRolesToAdd, setSelectedRolesToAdd] = useState([]);
  const [selectedMenusToAdd, setSelectedMenusToAdd] = useState([]);

  const extractData = (response) => {
    if (!response) return [];
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.data?.data)) return response.data.data;
    if (Array.isArray(response.data?.users)) return response.data.users;
    if (Array.isArray(response.data?.roles)) return response.data.roles;
    if (Array.isArray(response.data?.menus)) return response.data.menus;
    return [];
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [uRes, rRes, mRes] = await Promise.all([
          getAllUsers(),
          getAllRoles(),
          fetchAllMenus(),
        ]);

        const usersData = extractData(uRes) || [];
        setUsers(usersData);
        setFilteredUsers(usersData);
        setRoles(extractData(rRes) || []);
        setAllMenus(extractData(mRes) || []);
      } catch (err) {
        console.error("❌ Error loading initial data:", err.response?.data || err.message);
        setError(t('accessControl.failedToLoadInitialData'));
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredUsers(users);
    } else {
      const lowerQuery = searchQuery.toLowerCase();
      setFilteredUsers(
        users.filter(
          (u) =>
            u.username.toLowerCase().includes(lowerQuery) ||
            (u.email && u.email.toLowerCase().includes(lowerQuery))
        )
      );
    }
  }, [searchQuery, users]);

  useEffect(() => {
    const fetchUserRolesData = async () => {
      if (!selectedUser) {
        setUserRoles([]);
        setSelectedRolesToAdd([]);
        return;
      }
      try {
        const res = await getUserRoles(selectedUser.id);
        setUserRoles(extractData(res) || []);
        setSelectedRolesToAdd([]);
      } catch (err) {
        console.error("❌ Failed to load user roles:", err.response?.data || err.message);
        setError(t('accessControl.failedToLoadUserRoles'));
        setUserRoles([]);
      }
    };
    fetchUserRolesData();
  }, [selectedUser]);

  useEffect(() => {
    const fetchRoleMenusData = async () => {
      if (!selectedRole) {
        setRoleMenus([]);
        setSelectedMenusToAdd([]);
        return;
      }
      try {
        const res = await getMenusByRole(selectedRole.id);
        setRoleMenus(extractData(res) || []);
        setSelectedMenusToAdd([]);
      } catch (err) {
        console.error("❌ Failed to load role menus:", err.response?.data || err.message);
        setError(t('accessControl.failedToLoadRoleMenus'));
        setRoleMenus([]);
      }
    };
    fetchRoleMenusData();
  }, [selectedRole]);

  const handleAssignRoles = async () => {
    if (!selectedUser || selectedRolesToAdd.length === 0) return;
    try {
      await Promise.all(selectedRolesToAdd.map(roleId =>
        assignRoleToUser({ userId: selectedUser.id, roleId })
      ));
      const res = await getUserRoles(selectedUser.id);
      setUserRoles(extractData(res) || []);
      setSuccess(t('accessControl.rolesAssignedSuccessfully'));
      setSelectedRolesToAdd([]);
    } catch (err) {
      console.error("❌ Error assigning roles:", err.response?.data || err.message);
      setError(t('accessControl.failedToAssignRoles'));
    }
  };

  const handleRemoveRole = async (roleId) => {
    if (!selectedUser) return;
    try {
      await removeRoleFromUser({ userId: selectedUser.id, roleId });
      const res = await getUserRoles(selectedUser.id);
      setUserRoles(extractData(res) || []);
      setSuccess(t('accessControl.roleRemovedSuccessfully'));
    } catch (err) {
      console.error("❌ Error removing role:", err.response?.data || err.message);
      setError(t('accessControl.failedToRemoveRole'));
    }
  };

  const handleAssignMenus = async () => {
    if (!selectedRole || selectedMenusToAdd.length === 0) return;
    try {
      await Promise.all(selectedMenusToAdd.map(menuId =>
        assignMenuToRole({ roleId: selectedRole.id, menuId })
      ));
      const res = await getMenusByRole(selectedRole.id);
      setRoleMenus(extractData(res) || []);
      setSuccess(t('accessControl.menusAssignedSuccessfully'));
      setSelectedMenusToAdd([]);
    } catch (err) {
      console.error("❌ Error assigning menus:", err.response?.data || err.message);
      setError(t('accessControl.failedToAssignMenus'));
    }
  };

  const handleRemoveMenu = async (menuId) => {
    if (!selectedRole) return;
    try {
      await removeMenuFromRole({ roleId: selectedRole.id, menuId });
      const res = await getMenusByRole(selectedRole.id);
      setRoleMenus(extractData(res) || []);
      setSuccess(t('accessControl.menuRemovedSuccessfully'));
    } catch (err) {
      console.error("❌ Error removing menu:", err.response?.data || err.message);
      setError(t('accessControl.failedToRemoveMenu'));
    }
  };

  const handleCloseSnackbar = () => {
    setError("");
    setSuccess("");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getRoleName = (id) => roles.find((r) => r.id === id)?.name || id;

  const getMenuName = (id) => {
    const menu = allMenus.find((m) => m.id === id);
    return menu?.text || menu?.title || menu?.name || id;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
        <CircularProgress size={60} />
        <Typography sx={{ ml: 2 }}>{t('accessControl.loadingRoleManagement')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
        {t('accessControl.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('accessControl.subtitle')}
      </Typography>

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={handleCloseSnackbar} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={handleCloseSnackbar} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Paper sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '& .MuiTab-root': { py: 2, fontSize: '1rem', fontWeight: 'bold' }
          }}
        >
          <Tab icon={<SecurityIcon />} iconPosition="start" label={t('accessControl.userRolesTab')} />
          <Tab icon={<MenuIcon />} iconPosition="start" label={t('accessControl.roleMenusTab')} />
        </Tabs>
      </Paper>

      {/* Tab 1: User Roles */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={t('accessControl.selectUser')}
                subheader={t('accessControl.usersFound', { count: filteredUsers.length })}
                sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}
              />
              <CardContent sx={{ p: 2 }}>
                <TextField
                  fullWidth
                  placeholder={t('accessControl.searchUsers')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <List sx={{ maxHeight: 500, overflowY: "auto", border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {filteredUsers.length === 0 ? (
                    <ListItem>
                      <ListItemText primary={t('accessControl.noUsersFound')} secondary={t('accessControl.tryDifferentSearch')} />
                    </ListItem>
                  ) : (
                    filteredUsers.map((user) => (
                      <ListItem key={user.id} disablePadding divider>
                        <ListItemButton
                          selected={selectedUser?.id === user.id}
                          onClick={() => setSelectedUser(user)}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                {user.username}
                              </Box>
                            }
                            secondary={user.email}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={selectedUser ? t('accessControl.manageRolesFor', { username: selectedUser.username }) : t('accessControl.userRoleDetails')}
                subheader={selectedUser ? t('accessControl.addOrRemoveRoles') : t('accessControl.selectAUserToViewRoles')}
                sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
              />
              <CardContent sx={{ p: 3 }}>
                {!selectedUser ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.secondary' }}>
                    <PersonIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" component="div">{t('accessControl.noUserSelected')}</Typography>
                    <Typography variant="body2">{t('accessControl.pleaseSelectUser')}</Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        {t('accessControl.currentRoles')}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 2, bgcolor: 'grey.50', borderRadius: 1, minHeight: 60 }}>
                        {!userRoles || userRoles.length === 0 ? (
                          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>{t('accessControl.noRolesAssigned')}</Typography>
                        ) : (
                          userRoles.map((r) => (
                            <Chip
                              key={r.id}
                              icon={<CheckCircleIcon />}
                              label={r.name || r.roleName || t('accessControl.unknown')}
                              color="primary"
                              onDelete={() => handleRemoveRole(r.id)}
                              sx={{ px: 1 }}
                            />
                          ))
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {t('accessControl.assignNewRoles')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl fullWidth>
                          <InputLabel>{t('accessControl.selectRolesToAdd')}</InputLabel>
                          <Select
                            multiple
                            value={selectedRolesToAdd}
                            onChange={(e) => setSelectedRolesToAdd(e.target.value)}
                            input={<OutlinedInput label={t('accessControl.selectRolesToAdd')} />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={getRoleName(value)} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {roles
                              .filter((r) => !userRoles.find((ur) => ur.id === r.id))
                              .map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                  <Checkbox checked={selectedRolesToAdd.indexOf(r.id) > -1} />
                                  <ListItemText primary={r.name} />
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          disabled={selectedRolesToAdd.length === 0}
                          onClick={handleAssignRoles}
                          sx={{ height: 56, minWidth: 100 }}
                        >
                          {t('accessControl.add')}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Role Menus */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={t('accessControl.selectRole')}
                subheader={t('accessControl.configureMenusForRoles')}
                sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}
              />
              <CardContent sx={{ p: 2 }}>
                <List sx={{ maxHeight: 500, overflowY: "auto", border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {roles.map((role) => (
                    <ListItem key={role.id} disablePadding divider>
                      <ListItemButton
                        selected={selectedRole?.id === role.id}
                        onClick={() => setSelectedRole(role)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <RoleIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                              {role.name}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={selectedRole ? t('accessControl.manageMenusFor', { roleName: selectedRole.name }) : t('accessControl.roleMenuPermissions')}
                subheader={selectedRole ? t('accessControl.controlWhichMenus') : t('accessControl.selectARoleToConfigureMenus')}
                sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
              />
              <CardContent sx={{ p: 3 }}>
                {!selectedRole ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.secondary' }}>
                    <RoleIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" component="div">{t('accessControl.noRoleSelected')}</Typography>
                    <Typography variant="body2">{t('accessControl.pleaseSelectRole')}</Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        {t('accessControl.accessibleMenus')}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 2, bgcolor: 'grey.50', borderRadius: 1, minHeight: 60 }}>
                        {!roleMenus || roleMenus.length === 0 ? (
                          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>{t('accessControl.noMenusAssigned')}</Typography>
                        ) : (
                          roleMenus.map((m) => (
                            <Chip
                              key={m.id}
                              icon={<MenuIcon />}
                              label={m.text || m.title || m.name || t('accessControl.unknown')}
                              color="secondary"
                              variant="outlined"
                              onDelete={() => handleRemoveMenu(m.id)}
                              sx={{ px: 1 }}
                            />
                          ))
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {t('accessControl.grantMenuAccess')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl fullWidth>
                          <InputLabel>{t('accessControl.selectMenusToGrant')}</InputLabel>
                          <Select
                            multiple
                            value={selectedMenusToAdd}
                            onChange={(e) => setSelectedMenusToAdd(e.target.value)}
                            input={<OutlinedInput label={t('accessControl.selectMenusToGrant')} />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={getMenuName(value)} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {allMenus
                              .filter((m) => !roleMenus.find((rm) => rm.id === m.id))
                              .map((m) => (
                                <MenuItem key={m.id} value={m.id}>
                                  <Checkbox checked={selectedMenusToAdd.indexOf(m.id) > -1} />
                                  <ListItemText primary={m.text || m.title || m.name} />
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<AddIcon />}
                          disabled={selectedMenusToAdd.length === 0}
                          onClick={handleAssignMenus}
                          sx={{ height: 56, minWidth: 100 }}
                        >
                          {t('accessControl.grant')}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}